<div class="rounded-2xl w-full border border-gray-200 bg-white dark:border-gray-800 dark:bg-white/[0.03]">
  <div class="flex flex-wrap items-center justify-between gap-3 px-5 py-4 sm:px-6 sm:py-5">
    <div>
      <h3 class="text-base font-medium text-gray-800 dark:text-white/90">Client Access</h3>
      <p class="mt-1 text-sm text-gray-500 dark:text-gray-400">
        Control portal logins for this event's contacts.
      </p>
    </div>

    @if (state) {
      <app-checkbox
        [control]="enabledControl"
        [label]="state.enabled ? 'Access on' : 'Access off'"
        [inline]="true"
      ></app-checkbox>
    }
  </div>

  <div class="border-t border-gray-100 p-5 dark:border-gray-800 sm:p-6">
    @if (loading) {
      <div class="text-sm text-gray-500 dark:text-gray-400">Loading client access…</div>
    }

    @if (loadError) {
      <div class="rounded-md bg-error-50 p-3 text-sm text-error-700">{{ loadError }}</div>
    }

    @if (!loading && !loadError && state) {
      @if (state.enabled) {
        <p class="text-sm text-gray-600 dark:text-gray-300">
          Access enabled
          @if (state.enabled_by) {
            by <span class="font-medium">{{ state.enabled_by }}</span>
          }
          @if (state.enabled_at) {
            on <span class="font-medium">{{ state.enabled_at | date: 'dd MMM yyyy HH:mm' }}</span>
          }.
        </p>
      } @else {
        <p class="text-sm text-gray-500 dark:text-gray-400">
          Client access is currently off. Turn it on to invite this event's contacts to the portal.
        </p>
      }

      <!-- Contacts -->
      <div class="mt-6 space-y-3">
        @if (state.contacts.length === 0) {
          <div class="text-sm text-gray-500 dark:text-gray-400">No contacts on this event.</div>
        }

        @for (contact of state.contacts; track contact.id) {
          <div
            class="flex flex-col gap-3 rounded-lg border border-gray-200 p-4 dark:border-gray-800 sm:flex-row sm:items-center sm:justify-between"
          >
            <div class="min-w-0">
              <p class="truncate text-sm font-medium text-gray-800 dark:text-white/90">{{ contact.name }}</p>
              <p class="truncate text-xs text-gray-500 dark:text-gray-400">{{ contact.email }}</p>
              <div class="mt-2 flex flex-wrap items-center gap-2">
                @if (contact.has_password) {
                  <span class="inline-flex items-center rounded-full bg-green-50 px-2 py-0.5 text-xs font-medium text-green-700 dark:bg-green-900/30 dark:text-green-300">
                    Password set
                  </span>
                }
                @if (contact.has_pending_invitation) {
                  <span class="inline-flex items-center rounded-full bg-amber-50 px-2 py-0.5 text-xs font-medium text-amber-700 dark:bg-amber-900/30 dark:text-amber-300">
                    Invitation pending
                  </span>
                }
                @if (contact.access_active) {
                  <span class="inline-flex items-center rounded-full bg-blue-50 px-2 py-0.5 text-xs font-medium text-blue-700 dark:bg-blue-900/30 dark:text-blue-300">
                    Access active
                  </span>
                }
              </div>
            </div>

            <div class="flex flex-shrink-0 flex-wrap items-center gap-2">
              <app-button
                [type]="'button'"
                [style]="'secondary'"
                [size]="'normal'"
                [label]="isResending(contact) ? 'Sending…' : 'Resend invitation'"
                [disabled]="!state.enabled || isResending(contact)"
                (click)="resendInvitation(contact)"
              ></app-button>
              <app-button
                [type]="'button'"
                [style]="'secondary'"
                [size]="'normal'"
                [label]="isResetting(contact) ? 'Sending…' : 'Send password reset'"
                [disabled]="isResetting(contact)"
                (click)="sendPasswordReset(contact)"
              ></app-button>
            </div>
          </div>
        }
      </div>

      <!-- Audit trail -->
      <div class="mt-8">
        <h4 class="mb-3 text-sm font-medium text-gray-700 dark:text-gray-200">Activity</h4>

        @if (state.audit_entries.length === 0) {
          <div class="text-sm text-gray-500 dark:text-gray-400">No activity yet.</div>
        }

        <ul class="space-y-3">
          @for (entry of state.audit_entries; track entry.id) {
            <li class="rounded-lg border border-gray-200 bg-white p-4 dark:border-gray-800 dark:bg-white/[0.02]">
              <div class="flex flex-wrap items-center justify-between gap-2">
                <div class="flex items-center gap-2 text-xs text-gray-500 dark:text-gray-400">
                  <lucide-icon [name]="HistoryIcon" class="h-4 w-4"></lucide-icon>
                  <span class="font-medium text-gray-700 dark:text-gray-200">{{ entry.action_label }}</span>
                  @if (entry.staff_name) {
                    <span>·</span>
                    <span>{{ entry.staff_name }}</span>
                  }
                  @if (entry.contact_name) {
                    <span>·</span>
                    <span>{{ entry.contact_name }}</span>
                  }
                  <span>·</span>
                  <span>{{ entry.created_at | date: 'dd MMM yyyy HH:mm' }}</span>
                </div>
              </div>
              <p class="mt-2 whitespace-pre-wrap text-sm text-gray-800 dark:text-gray-100">{{ entry.description }}</p>
            </li>
          }
        </ul>
      </div>
    }
  </div>
</div>
